import type { PartialBlock } from '@blocknote/core';

/** Split a leading `# title` from markdown pasted as a full page. */
export function extractLeadingH1(markdown: string): { title: string; body: string } | null {
  const lines = markdown.trim().split('\n');
  let i = 0;
  while (i < lines.length && lines[i]!.trim() === '') {
    i++;
  }
  if (i >= lines.length) {
    return null;
  }

  const match = lines[i]!.match(/^#\s+(.+)$/);
  if (!match) {
    return null;
  }

  const title = match[1]!.trim();
  if (!title) {
    return null;
  }

  i++;
  while (i < lines.length && lines[i]!.trim() === '') {
    i++;
  }

  return { title, body: lines.slice(i).join('\n').trimStart() };
}

export function isEmptyParagraphBlock(block: PartialBlock | undefined): boolean {
  if (!block || block.type !== 'paragraph') {
    return false;
  }
  const content = block.content;
  return !content || (Array.isArray(content) && content.length === 0);
}

/** True when the paste should replace the whole page body (and maybe the title). */
export function isEntirePageBodySelected(
  documentBlocks: PartialBlock[],
  selectedBlockIds: string[],
): boolean {
  if (documentBlocks.length === 0) {
    return true;
  }

  if (documentBlocks.length === 1 && isEmptyParagraphBlock(documentBlocks[0])) {
    return true;
  }

  const topLevelIds = documentBlocks.map((block) => block.id).filter((id): id is string => Boolean(id));
  if (topLevelIds.length === 0 || selectedBlockIds.length === 0) {
    return false;
  }

  const selected = new Set(selectedBlockIds);
  return topLevelIds.every((id) => selected.has(id));
}

/** Lightweight markdown sniff — aligned with BlockNote's paste detection. */
export function looksLikeMarkdown(text: string): boolean {
  const src = text.trim();
  if (!src) {
    return false;
  }

  return (
    /(^|\n) {0,3}#{1,6} \S/.test(src) ||
    /(^|\n)\s{0,5}[-*+]\s+\S/.test(src) ||
    /(^|\n)\s{0,5}\d+\.\s+\S/.test(src) ||
    /\*\*[^*\n]+\*\*/.test(src) ||
    /(^|\n)```/.test(src) ||
    /\[[^\]]+\]\(https?:\/\//.test(src) ||
    /(^|\n) {0,3}>\s+\S/.test(src)
  );
}
